import React, { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { PageLoader } from "../components";
import { Wrapper } from "../container/Wrapper";
import { AuthRoute } from "./AuthRoute";
import { PrivateRoute } from "./PrivateRoute";
import LandingEdits from "../pages/landing/LandingEdits";


// Admin Pages

const AdminLogin = React.lazy(() => import("../pages/AdminLogin"));
const Dashboard = React.lazy(() => import("../pages/dashboard/Dashboard"));


// Student Module
const StudentList = React.lazy(() => import("../pages/student/StudentList"));
const StudentSingle = React.lazy(() => import("../pages/student/StudentSingle"));
const VerificationManagement = React.lazy(() => import("../pages/student/VerificationManagement"));

// Batch Module
const BatchList = React.lazy(() => import("../pages/batch/BatchList"));
const BatchSingle = React.lazy(() => import("../pages/batch/BatchSingle"));

// Course Module
const CourseList = React.lazy(() => import("../pages/course/CourseList"));

// Request Module
const RequestList = React.lazy(() => import("../pages/request/RequestList"));
const RequestSingle = React.lazy(() => import("../pages/request/RequestSingle"));

// Member Module
const MemberList = React.lazy(() => import("../pages/member/MemberList"));
const MemberSingle = React.lazy(() => import("../pages/member/MemberSingle"));
const MemberProfile = React.lazy(() => import("../pages/member/MemberProfile"));

// Activity Log Module
const ActivityLogs = React.lazy(() => import("../pages/ActivityLogs"));

// Announcement Module
const AnnouncementList = React.lazy(() => import("../pages/announce/AnnouncementList"));

// Attendance Module
const AttendanceList = React.lazy(() => import("../pages/attendance/AttendanceList"));

// Performance Module
const PerformanceHome = React.lazy(() => import("../pages/performance/PerformanceHome"));
const PerformanceList = React.lazy(() => import("../pages/performance/PerformanceList"));
const IndividualPerformanceList = React.lazy(() => import("../pages/performance/IndividualPerformanceList"));

// Question Module
const QuestionList = React.lazy(() => import("../pages/question/QuestionList"));
const QuestionCreate = React.lazy(() => import("../pages/question/QuestionCreate"));

const Payments = React.lazy(() => import("../pages/Payments"));
const FulfilmentList = React.lazy(() => import("../pages/fulfilment/FulfilmentList"));
const FulfilmentDetail = React.lazy(() => import("../pages/fulfilment/FulfilmentDetail"));
const PostalFeesList = React.lazy(() => import("../pages/fulfilment/PostalFeesList"));
const PickUpsList = React.lazy(() => import("../pages/fulfilment/PickUpsList"));
const AllInventory = React.lazy(() => import("../pages/inventory/InventoryList"));
const Birthdays = React.lazy(() => import("../pages/Birthdays"));
const Categories = React.lazy(() => import("../pages/Categories"));
const InventoryStatement = React.lazy(() => import("../pages/inventory/InventoryStatement"));
const AdminForgotPassword = React.lazy(() => import("../pages/AdminForgotPassword"));
const ResetPassword = React.lazy(() => import("../pages/AdminChangePassword"));
const ChangePassword = React.lazy(() => import("../pages/ChangePassword"));

// Test Module
const TestList = React.lazy(() => import("../pages/tests/TestList"));
const TestSolution = React.lazy(() => import("../pages/tests/TestSolution"));

// Assignment Module
const AssignmentList = React.lazy(() => import("../pages/assignments/AssignmentList"));
const AssignmentSubmissions = React.lazy(() => import("../pages/assignments/AssignmentSubmissions"));

// Article Module
const ArticleList = React.lazy(() => import("../pages/article/ArticleList"));
const ArticleCreate = React.lazy(() => import("../pages/article/ArticleCreate"));

//Analytics Module
const Analytics = React.lazy(() => import("../pages/Analytics"));
const CartMarketingAnalytics = React.lazy(() => import("../pages/cartMarketingAnalytics"));

// Chat Module
const ChatList = React.lazy(() => import("../pages/chat/ChatList"));

// Access Control Module
const PermissionList = React.lazy(() => import("../pages/permission/PermissionList"));

// Email Templates
const EmailTemplates = React.lazy(() => import("../pages/emailTemplates/EmailTemplates"));

// Invoices
const InvoiceList = React.lazy(() => import("../pages/invoice/InvoiceList"));

const Orders = React.lazy(() => import("../pages/Orders"));
const CartPage = React.lazy(() => import("../pages/Carts"));

const NotFound = React.lazy(() => import("../pages/NotFound"));
const NotPermitted = React.lazy(() => import("../pages/NotPermitted"));

function AppRouter() {
  return (
    <BrowserRouter>
      <Wrapper>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route element={<AuthRoute />}>
              <Route index element={<AdminLogin />} />
              <Route path="forgot-password" element={<AdminForgotPassword />} />
              <Route path="reset-password/:token" element={<ResetPassword />} />
            </Route>
            <Route element={<PrivateRoute />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="students" element={<StudentList />} />
              <Route path="students/:id" element={<StudentSingle />} />
              <Route path="verification" element={<VerificationManagement />} />
              <Route path="payments" element={<Payments />} />
              <Route path="requests" element={<RequestList />} />
              <Route path="requests/:id" element={<RequestSingle />} />
              <Route path="ims" element={<Navigate to="/fulfilment" replace />} />
              <Route path="fulfilment" element={<FulfilmentList />} />
              <Route path="fulfilment/postal-fees" element={<PostalFeesList />} />
              <Route path="fulfilment/orders/:cartOrderId" element={<FulfilmentDetail />} />
              <Route path="fulfilment/:sku_code" element={<FulfilmentDetail />} />
              <Route path="pickups" element={<PickUpsList />} />
              <Route path="batches" element={<BatchList />} />
              <Route path="batches/:id" element={<BatchSingle />} />
              <Route path="courses" element={<CourseList />} />

              <Route path="birthdays" element={<Birthdays />} />
              <Route path="profile" element={<MemberProfile />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="announcements" element={<AnnouncementList />} />
              <Route path="attendance" element={<AttendanceList />} />
              <Route path="performance">
                <Route index element={<PerformanceHome />} />
                <Route path="batch" element={<PerformanceList />} />
                <Route path="individual" element={<IndividualPerformanceList />} />
              </Route>
              <Route path="questions">
                <Route index element={<QuestionList />} />
                <Route path="create" element={<QuestionCreate />} />
              </Route>

              <Route path="tests">
                <Route index element={<TestList />} />
                <Route path="solution" element={<TestSolution />} />
              </Route>

              <Route path="assignments">
                <Route index element={<AssignmentList />} />
                <Route path=":id/submissions" element={<AssignmentSubmissions />} />
              </Route>

              <Route path="articles">
                <Route index element={<ArticleList />} />
                <Route path="create" element={<ArticleCreate />} />
              </Route>

              <Route path="invoices">
                <Route index element={<InvoiceList />} />
              </Route>

              <Route path="orders" element={<Orders />} />
              <Route path="carts" element={<CartPage />} />
              <Route path="cart-marketing" element={<CartMarketingAnalytics />} />

              <Route path="inventory" element={<AllInventory />} />
              <Route path="categories" element={<Categories />} />
              <Route path="inventory-statement" element={<InventoryStatement />} />
              <Route path="activities" element={<ActivityLogs />} />

              <Route path="members" element={<MemberList />} />
              <Route path="members/:id" element={<MemberSingle />} />

              <Route path="permission" element={<PermissionList />} />

              <Route path="landing" element={<LandingEdits />} />

              <Route path="chats" element={<ChatList />} />

              {/* for email templates  */}
              <Route path="email-templates" element={<EmailTemplates />} />
            </Route>
            <Route path="admin" element={<Navigate to="/" />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="access-denied" element={<NotPermitted />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Wrapper>
    </BrowserRouter>
  );
}

export default AppRouter;
